import {
  AppBar,
  AvatarGroup,
  Box,
  Button,
  Card,
  Divider,
  Fab,
  Grid,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useEffect, useState } from "react";
import { Wrapper } from "./styles";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import Modal from "../../components/Modal";
import InputForm from "../../components/controls/InputForm";
import ControlForm from "../../components/ControlForm";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ICreateDepartmentModel,
  ICreateDepartmentReq,
  IDepartment
} from "../../models/department";
import { TagInput } from "../WorkflowManagement/components/TagInput/TagInput";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATH } from "../../common/constants/app.constant";
import DepartmentService from "../../services/api/department.service";
import { toggleMessage } from "../../components/Toast/Toast";
import Avatar from "../../components/Avatar";
import { authTokens } from "../../services/services";
import {
  DepartMentPermissionModel,
  FeatureModel
} from "../../models/common/models.enum";
import { useApi } from "../../hooks/useApi.hook";
import LoadingOverlayWrapper from "react-loading-overlay-ts";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchNoResult from "../Member/components/SearchNoResult";
import SearchInput from "../Member/components/SearchInput";
import { Helmet } from "react-helmet";
import { IBasePaging } from "../../models/common/models.type";
import { IFeaturePermission } from "../../models/user/models.roles";
import RoleService from "../../services/api/role.service";
import { AddRounded, GroupAddRounded } from "@mui/icons-material";
import { theme } from "../../theme/theme";
import variableStyles from "../../theme/variable-styles";
import { useTranslation } from "react-i18next";

const Department = () => {
  const { t } = useTranslation();

  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [userId, setUserId] = useState<string | undefined>();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [data, setData] = useState<IDepartment[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [paging, setPaging] = useState<IBasePaging | null>();
  const [searchState, setSearchState] = useState<"SUCCESS" | "NONE" | null>(
    null
  );
  const navigate = useNavigate();
  const getPage = useApi<IDepartment[]>({ isFetch: true });
  const createDepartment = useApi<IDepartment[]>({});
  const getFeaturePersmission = useApi<IFeaturePermission[]>({ isFetch: true });
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  function fetchData(keySearch?: string, pageIndex?: number) {
    getPage.request(
      DepartmentService.getPage({
        CurrentPage: pageIndex ?? 1,
        PageSize: 10,
        KeySearch: keySearch
      })
    );
    setSearchState(null);
    if (!keySearch) {
      setInputValue("");
    }
  }

  useEffect(() => {
    async function getUserInfo() {
      const user = await authTokens.getUser();
      user && setUserId(user.userId);
    }
    getUserInfo();
    fetchData();
  }, []);

  useEffect(() => {
    if (userId)
      getFeaturePersmission.request(
        RoleService.getFeaturePemissionByUserId(userId)
      );
  }, [userId]);

  useEffect(() => {
    if (getFeaturePersmission.data) {
      setPermissions(
        getFeaturePersmission.data.find(
          f => f.feature === FeatureModel.Department
        )?.permissions ?? []
      );
    }
  }, [getFeaturePersmission.data]);

  useEffect(() => {
    if (getPage.data) {
      setData(getPage?.data ?? []);
      setPaging(getPage?.pagging);
      if (getPage.data.length && inputValue.length) {
        setSearchState("SUCCESS");
      } else if (!getPage.data.length && inputValue.length) {
        setSearchState("NONE");
      }
    }
  }, [getPage.data]);

  useEffect(() => {
    if (createDepartment.success) {
      toggleMessage({
        type: "success",
        message: createDepartment.message ?? ""
      });
      handleClose();
      fetchData();
    }
    if (createDepartment.error) {
      toggleMessage({
        type: "error",
        message: getPage?.error
      });
    }
  }, [createDepartment.success, createDepartment.error]);

  useEffect(() => {
    if (getPage.error) {
      toggleMessage({
        type: "error",
        message: getPage?.error
      });
    }
  }, [getPage.error]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("Department.PleaseEnterGroupName")),
    userName: Yup.string().required(t("Department.PleaseEnterUsername")),
    leaderId: Yup.array()
      .required(t("Department.PleaseSelectGroupAdmin"))
      .min(1, t("Department.PleaseSelectGroupAdmin"))
  }) as any;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ICreateDepartmentModel>({
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = (request: ICreateDepartmentModel) => {
    const req: ICreateDepartmentReq = {
      name: request.name,
      userName: request.userName,
      leaderId: request.leaderId[0].id,
      memberIds: request.memberIds?.map(member => member.id)
    };
    createDepartment.request(DepartmentService.create(req));
  };

  function handleClose() {
    setOpenCreate(false);
    reset();
  }

  function handleSearch() {
    if (inputValue.length > 0) {
      fetchData(inputValue, 1);
    } else {
      fetchData();
      setSearchState(null);
    }
  }

  return (
    <LoadingOverlayWrapper
      active={getPage.loading}
      text={t("Common.LoadingDataPleaseWait")}
    >
      <Wrapper>
        <Helmet>
          <title> {t("Department.AccountManagementGroupTitle")}</title>
        </Helmet>

        <AppBar position="static" color="default">
          <Toolbar>
            <Box flexGrow={1}>
              <SearchInput
                input={inputValue}
                placeholder={t("Department.SearchGroupsPlaceholder")}
                isSuccess={searchState === "SUCCESS"}
                search={handleSearch}
                clear={() => fetchData()}
                value={inputValue}
                countResult={data?.length ?? 0}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setInputValue(event.target.value);
                }}
              />
            </Box>
            {permissions.includes(DepartMentPermissionModel.Create) && (
              <Button
                variant="contained"
                size="small"
                sx={{
                  borderRadius: "4px",
                  backgroundColor: variableStyles.MainColor
                }}
                onClick={_ => setOpenCreate(true)}
              >
                {matches ? (
                  <GroupAddRounded />
                ) : (
                  <Typography>
                    {" "}
                    {t("Department.CreateUserGroupButton")}
                  </Typography>
                )}
              </Button>
            )}
            <Modal
              title={t("Department.CreateUserGroupModalTitle")}
              textSubmit={t("Modal.SubmitText")}
              textClose={t("Modal.CancelText")}
              open={openCreate}
              onSubmit={handleSubmit(onSubmit)}
              onClose={handleClose}
              loadingState={createDepartment.loading}
            >
              <form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px"
                }}
              >
                <ControlForm
                  title={t("Department.GroupNameLabel")}
                  isRequired
                  classname="brand-form"
                >
                  <InputForm
                    placeholder={t("Department.EnterGroupName")}
                    required
                    name="name"
                    errors={errors}
                    control={control}
                    size="small"
                    disabled={createDepartment.loading}
                  />
                </ControlForm>
                <ControlForm title="Username" isRequired classname="brand-form">
                  <InputForm
                    placeholder="@tennhom"
                    required
                    name="userName"
                    errors={errors}
                    control={control}
                    size="small"
                    disabled={createDepartment.loading}
                  />
                </ControlForm>
                <ControlForm
                  title={t("Department.GroupAdminLabel")}
                  isRequired
                  classname="brand-form"
                >
                  <TagInput
                    name={"leaderId"}
                    control={control}
                    errors={errors}
                    isError={!!errors.leaderId}
                    placeholder={t("Department.TypeToSearchPlaceholder")}
                    limit={1}
                  />
                </ControlForm>
                <ControlForm
                  title={t("Department.MembersLabel")}
                  infor={t("Department.MembersNoteCanBeAddedLater")}
                  classname="brand-form"
                >
                  <TagInput
                    name={"memberIds"}
                    control={control}
                    errors={errors}
                    isError={false}
                    placeholder={t("Department.AddMembersPlaceholder")}
                    // optionDisabled={data}
                  />
                </ControlForm>
              </form>
            </Modal>
          </Toolbar>
        </AppBar>

        {searchState === "NONE" && (
          <SearchNoResult
            keySearch={inputValue}
            title={t("Department.SearchNoResultTitleDepartment")}
            clearSearch={() => fetchData()}
          />
        )}

        <Box
          sx={{
            margin: "12px 14px"
          }}
        >
          <Grid container spacing={2} columns={16}>
            {!data.length ? (
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  alignContent: "center",
                  height: "75vh",
                  width: "100vw"
                }}
              >
                {t("Department.EmptyList")}
              </Typography>
            ) : (
              data.map((item, idx) => (
                <Grid key={item.id} item xs={matches ? 16 : 8}>
                  <Card
                    sx={{ padding: "16px" }}
                    onClick={() =>
                      navigate(`${ROUTE_PATH.ACCOUNT_DEPARTMENT}/${item.id}`)
                    }
                  >
                    <Box display="flex">
                      <Box
                        sx={{
                          backgroundColor: "#e8efee",
                          borderRadius: "8px",
                          width: "44px",
                          height: "44px",
                          textAlign: "center",
                          alignContent: "center",
                          fontSize: "16px",
                          fontWeight: "600"
                        }}
                      >
                        {idx + 1 < 10 ? "0" : ""}
                        {idx + 1}
                      </Box>
                      <Box sx={{ marginLeft: "12px", flexGrow: 1 }}>
                        <Typography variant="subtitle1" className="card-name">
                          {item.name}
                        </Typography>
                        <Box display="flex">
                          <Typography
                            variant="subtitle2"
                            className="card-username"
                          >
                            {item.userName}
                          </Typography>
                        </Box>
                      </Box>
                      <EastRoundedIcon />
                    </Box>

                    <Box display="flex" marginTop="10px">
                      <Box flexGrow={1} display="flex" flexDirection="column">
                        <Typography
                          className="card-username"
                          variant="caption"
                          flexGrow={1}
                        >
                          {t("Department.AdminLabelUppercase")}
                        </Typography>
                        {!!item.infoLeader ? (
                          <Box
                            display="flex"
                            alignItems="center"
                            marginTop="2px"
                          >
                            <Avatar
                              src={item.infoLeader?.avatar}
                              width={24}
                              height={24}
                            />
                            <Typography
                              variant="caption"
                              marginLeft="8px"
                              className="card-leader"
                            >
                              {item.infoLeader?.fullName}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" marginTop="4px">
                            {t("Department.NoAdminYet")}
                          </Typography>
                        )}
                      </Box>

                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ margin: "0 12px" }}
                      />

                      <Box
                        flexGrow={1}
                        flexDirection="column"
                        display="flex"
                        alignItems="end"
                      >
                        <Typography
                          className="card-username"
                          variant="caption"
                          flexGrow={1}
                        >
                          {item.infoMembers.length}{" "}
                          {t("Department.MembersSuffix")}
                        </Typography>
                        {item.infoMembers.length === 0 ? (
                          <Typography variant="caption" marginTop="4px">
                            {t("Department.NoMembersYet")}
                          </Typography>
                        ) : (
                          <Box display="flex" justifyContent="space-between">
                            <AvatarGroup
                              total={item.infoMembers.length}
                              max={4}
                              sx={{
                                "& .MuiAvatar-root": {
                                  width: 24,
                                  height: 24,
                                  fontSize: 15
                                }
                              }}
                            >
                              {item.infoMembers.map(info => (
                                <Avatar
                                  key={info.id}
                                  src={info?.avatar}
                                  width={24}
                                  height={24}
                                />
                              ))}
                            </AvatarGroup>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
          {paging && paging.currentPage !== paging.totalPages && (
            <Button
              size="small"
              onClick={() => fetchData(undefined, paging.currentPage + 1)}
            >
              {t("Department.LoadMore")}
              <ExpandMoreRoundedIcon />
            </Button>
          )}
        </Box>
      </Wrapper>
    </LoadingOverlayWrapper>
  );
};

export default Department;

//30->21.5
//24->15.5
//33->24.5
//13->4.5
//30->21.5
//29->20.5
//30->21.5
//39->30.5
//23->14.5
//33->24.5
